import { API, deleteRequest, getRequest, postRequest, putRequest } from '@/api/api';

import type {
  DeleteServiceRolePermissionsPayload,
  ListServicePeopleParams,
  PageResponse,
  RolePermissions,
  SeatPermissions,
  ServicePerson,
  PlanSeat,
  ServicePlanSeat,
  UpdatePersonPermissionsPayload,
  UpsertServiceRolePermissionsPayload,
} from './permissions.dto';

export const getServiceRolePermissions = async (serviceId: string): Promise<RolePermissions[]> => {
  const res = await getRequest<RolePermissions[]>(
    API.PEOPLE,
    `/permissions/services/${serviceId}/roles`,
  );
  return res.data;
};

export const getBasePermissions = async (): Promise<string[]> => {
  const res = await getRequest<string[]>(API.PEOPLE, '/permissions/base');
  return res.data;
};

/**
 * The per-seat-type permission allow-lists. Global, not service-scoped — which seat a
 * given vessel or person sits in is what makes a list relevant to them.
 */
export const getSeatPermissions = async (): Promise<SeatPermissions[]> => {
  const res = await getRequest<SeatPermissions[]>(API.PEOPLE, '/permissions/seats');
  return res.data ?? [];
};

/**
 * The seat the service's plan pays for — the allow-list the server measures a role save
 * against. Unlike `GET /subscriptions`, anyone on the service may read it, so a crew member
 * editing roles sees the plan they are really on. `null` for a service no plan gates.
 */
export const getServicePlanSeat = async (serviceId: string): Promise<PlanSeat> => {
  const res = await getRequest<ServicePlanSeat>(
    API.PEOPLE,
    `/permissions/services/${serviceId}/plan-seat`,
  );
  return {
    seat: res.data?.seat_type || null,
    manuallyGranted: res.data?.manually_granted ?? false,
  };
};

export const upsertServiceRolePermissions = async (
  serviceId: string,
  payload: UpsertServiceRolePermissionsPayload,
): Promise<void> => {
  await postRequest<void, UpsertServiceRolePermissionsPayload>(
    API.PEOPLE,
    `/permissions/services/${serviceId}/roles`,
    payload,
  );
};

export const deleteServiceRolePermissions = async (
  serviceId: string,
  payload: DeleteServiceRolePermissionsPayload,
): Promise<void> => {
  await deleteRequest<void, DeleteServiceRolePermissionsPayload>(
    API.PEOPLE,
    `/permissions/services/${serviceId}/roles`,
    payload,
  );
};

export const listServicePeople = async (
  serviceId: string,
  params: ListServicePeopleParams = {},
): Promise<PageResponse<ServicePerson>> => {
  const { limit = 100, page = 1, search, role } = params;
  const res = await getRequest<PageResponse<ServicePerson>>(API.PEOPLE, '', false, {
    service_id: serviceId,
    limit,
    page,
    ...(search ? { search } : {}),
    ...(role && role.length ? { role } : {}),
  });
  return res.data;
};

export const updatePersonPermissions = async (
  payload: UpdatePersonPermissionsPayload,
): Promise<void> => {
  await putRequest<void, UpdatePersonPermissionsPayload>(API.PEOPLE, '/permissions', payload);
};
